import { useUiOverlayContext } from "@/components/common/ui-overlay-provider";
import { Upload3dDataDropHandler } from "@/components/common/upload-3d-data-drop-handler";
import { AppColorBar } from "@/components/r3f/utils/app-color-bar";
import { ToolsHelpBanners } from "@/components/ui/help-banners/tools-help-banners";
import { useMinimapHeight } from "@/hooks/use-minimap-height";
import { MeasurementTouchToolbar } from "@/tools/multi-point-measures/measurement-touch-toolbar";
import { grey } from "@faro-lotv/flat-ui";
import { Box, Stack } from "@mui/material";
import { useState, type JSX } from "react";
import { CaptureDateFilter } from "./capture-date-filter/capture-date-filter";
import { EditWaypointsOverlay } from "./edit-waypoints/edit-waypoints-overlay";
import { useWaypointInfoForDragEvent } from "./use-waypoint-info-for-drag-event";

/**
 * @returns the Overlay for SheetMode
 */
export function SheetOverlay(): JSX.Element {
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const height = useMinimapHeight({ container });
  const { bottomRightAvailableHeight: maxHeight } = useUiOverlayContext();

  const getWaypointInfoForDragEvent = useWaypointInfoForDragEvent();

  return (
    <Upload3dDataDropHandler
      getWaypointInfoForDragEvent={getWaypointInfoForDragEvent}
    >
      <Stack
        direction="column"
        sx={{
          justifyContent: "space-between",
          height: "100%",
          width: "100%",
          p: 2,
        }}
        ref={setContainer}
      >
        {/* Mirrors overview-overlay.tsx: a row claiming the top-left, banners below */}
        <Stack direction="column" sx={{ gap: 2, width: "100%" }}>
          <Stack
            direction="row"
            sx={{
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              width: "100%",
            }}
          >
            <Stack direction="row" sx={{ gap: 1.25, overflow: "hidden" }}>
              <CaptureDateFilter />
            </Stack>
          </Stack>
          <ToolsHelpBanners />
        </Stack>
        <Stack
          direction="row"
          sx={{
            justifyContent: "space-between",
            height,
            maxHeight,
          }}
        >
          {/* This box is only used for equally spacing the three elements in the flex container */}
          <Box component="div" />
          <Stack
            aria-label="edit waypoints toolbar"
            direction="column"
            sx={{ justifyContent: "flex-end" }}
          >
            <EditWaypointsOverlay />
            <MeasurementTouchToolbar />
          </Stack>
          <Stack direction="row" sx={{ alignContent: "stretch" }}>
            <AppColorBar
              sx={{
                position: "relative",
                p: "4px",
                backgroundColor: grey[1000],
                borderWidth: "1px",
                borderColor: `${grey[0]}33`,
                borderRadius: "4px",
              }}
            />
          </Stack>
        </Stack>
      </Stack>
    </Upload3dDataDropHandler>
  );
}
